import {
  ArticleBullets,
  ArticleImage,
  ArticleLayout,
  ArticleParagraph,
  ArticleSection,
  QuickVerdict,
} from '../../../components/marketing/ArticleLayout';
import {
  ARTICLE_IMAGES,
  ARTICLE_SUMMARY_BY_SLUG,
} from '../../../lib/content/articles';

const article = ARTICLE_SUMMARY_BY_SLUG['ask-your-financial-questions-in-plain-english'];

export default function PlainEnglishQuestionsPage() {
  return (
    <ArticleLayout
      article={article}
      relatedSlugs={['your-daily-freedom-number', 'how-we-turn-transaction-cleanup-into-better-planning']}
    >
      <QuickVerdict
        title="Quick take"
        items={[
          'The useful version of an AI planning assistant is grounded in your household context, not generic financial trivia.',
          'Per Diem’s AI layer is most valuable when it helps users interrogate the same budgets, balances, and plans the rest of the product is already using.',
          'Plain-English access matters because not every user wants to navigate a financial model through menus alone.',
        ]}
      />

      <ArticleSection title="AI is only interesting here if it knows what household it is talking about">
        <ArticleParagraph>
          General financial advice on the internet is already abundant. The question is whether an
          AI layer can help a household reason about its own situation without forcing it to translate
          everything into spreadsheet language first.
        </ArticleParagraph>
        <ArticleParagraph>
          That is the promise of plain-English querying in Per Diem. The user should be able to ask
          something natural and get an answer grounded in the same financial context the product is
          already tracking.
        </ArticleParagraph>
      </ArticleSection>

      <ArticleImage
        source={ARTICLE_IMAGES.mobileAskAi}
        alt="Per Diem mobile Ask AI screen"
        caption="The AI layer matters when it helps the household ask plain-English questions about its actual plan, not when it simply produces financial-sounding text."
        variant="phone"
      />

      <ArticleSection title="Why conversational access helps">
        <ArticleBullets
          items={[
            'It lowers the barrier to exploring the model when the user does not know exactly where to click.',
            'It makes it easier to ask situational questions in normal language.',
            'It helps users inspect the implications of their data without needing to become product power users first.',
          ]}
        />
      </ArticleSection>

      <ArticleSection title="The hard part is grounding, not style">
        <ArticleParagraph>
          A friendly answer is easy to fake. A grounded answer is much harder. The value of an AI
          planning assistant depends on whether it has access to the right household context and
          whether the product is honest about what the answer can and cannot do.
        </ArticleParagraph>
        <ArticleParagraph>
          In Per Diem, the assistant is useful when it acts as another interface to the planning
          model, not as a replacement for the model.
        </ArticleParagraph>
      </ArticleSection>

      <ArticleImage
        source={ARTICLE_IMAGES.mobileFirePlan}
        alt="Per Diem mobile FIRE plan"
        caption="A conversational layer works best when it helps users interrogate a real plan instead of generating generic retirement content."
        variant="phone"
      />

      <ArticleSection title="The goal is more understandable planning">
        <ArticleParagraph>
          The right role for AI in a product like this is not theatrical intelligence. It is easier
          access to the household’s own numbers, assumptions, and tradeoffs. That is what makes
          plain-English questions feel worthwhile instead of ornamental.
        </ArticleParagraph>
      </ArticleSection>
    </ArticleLayout>
  );
}
